import { Workbook } from "@progress/kendo-ooxml";
import { saveAs } from "@progress/kendo-file-saver";

// Excel export for the Assigned Clients grid. Unlike the Parent Renewal
// Dashboard's export, there is no business-supplied summary layout to match --
// this just mirrors the grid's own columns, one row per client, with
// sub-clients flattened under their parent the same way the grid nests them.
export const EXPORT_COLUMNS = [
  "Company ID",
  "Company Name",
  "Parent ID",
  "Broker",
  "Benefit Rep",
  "Sales rep",
  "Current Cost",
  "Percentage Increase",
  "Notification Wave",
  "Notification Sent",
  "Status",
  "Renewal",
  "Carrier",
  "Submission Record",
];

// Matches parentClientIdCell in ClientDashboard.jsx: a client that is its own
// parent (or has no parent at all) shows as "-" rather than repeating its id.
const parentIdCell = (row) =>
  row.parentClientId == null || row.parentClientId === row.clientId
    ? "-"
    : row.parentClientId;

const rowCells = (row, { isChild }) => [
  { value: row.clientId },
  { value: row.clientName },
  { value: isChild ? row.parentClientId : parentIdCell(row) },
  { value: row.broker || "" },
  { value: row.benefitRepName || "" },
  { value: row.salesRepName || "" },
  { value: row.currentRate ?? 0, format: "$#,##0.00" },
  { value: row.percentageIncrease ?? 0, format: "0.00%" },
  { value: row.wave || "" },
  { value: row.notificationSent || "" },
  { value: row.oeStatusDesc || "" },
  { value: row.renewalHandlingMode || "" },
  { value: row.carrierName || "" },
  { value: row.submissionRecord || "" },
];

/**
 * Turns the grid's rows (each optionally carrying subClients) into workbook
 * rows. Exported separately from the save so it can be tested without a
 * browser.
 */
export const buildExportRows = (rows) => {
  const ordered = [...(rows || [])].sort((a, b) =>
    (a.clientName || "").localeCompare(b.clientName || ""),
  );

  const bodyRows = [];

  ordered.forEach((parent) => {
    bodyRows.push({ cells: rowCells(parent, { isChild: false }) });

    (parent.subClients || []).forEach((child) => {
      bodyRows.push({
        // level 1 is what makes Excel draw the expander against the parent above.
        level: 1,
        cells: rowCells(child, { isChild: true }),
      });
    });
  });

  return { bodyRows };
};

export const buildWorkbook = (rows, sheetName = "Assigned Clients") => {
  const { bodyRows } = buildExportRows(rows);

  return new Workbook({
    sheets: [
      {
        name: sheetName,
        freezePane: { rowSplit: 1 },
        columns: EXPORT_COLUMNS.map(() => ({ width: 140 })),
        rows: [
          { cells: EXPORT_COLUMNS.map((title) => ({ value: title, bold: true })) },
          ...bodyRows,
        ],
      },
    ],
  });
};

export const saveClientDashboardWorkbook = async (rows, fileName) => {
  const dataUrl = await buildWorkbook(rows).toDataURL();
  saveAs(dataUrl, fileName);
};
